# Route: /admin/users/:userId
# User detail: Keycloak info + profile + role assignments

page-admin-user-detail:
  type: Page
  archetype: detail
  meta:
    title: $t.admin.users.detail_title
    auth: required
  params:
    userId:
      type: string
      required: true
  data:
    user:
      model: UserDetail
      key: $route.userId
      refresh: on_mount
    # F62 role-assignment contract: (roleName, optional domainId). When
    # domainId is omitted the server scopes the assignment to the tenant.
    assignForm:
      model: AssignRoleForm
      initial:
        roleName: ""
    removeForm:
      model: RemoveRoleForm
      initial:
        assignmentId: ""
    # Options for the role-assignment picker: the roles that can be granted
    # (GET /api/users/roles?assignable=true). The catalogue also holds the tenant's templates,
    # which POST /api/users/{userId}/roles refuses with a 400, so the picker never offers one.
    roles:
      model: AssignableRole[]
      refresh: on_mount
    # The assignments this user HOLDS, one row per (role, domain) — one binding for the roles
    # table and the remove picker so the two cannot disagree. A role held on two domains is two
    # rows and two picker options, each naming its domain.
    heldRoles:
      model: RoleAssignment[]
      key: $route.userId
      refresh: on_mount
  spatial:
    display: flex
    direction: column
    gap: $spacing.lg
    padding: $spacing.lg
    padding_inline: $spacing.xl
    overflow: auto
    responsive:
      sm:
        padding: $spacing.sm
        padding_inline: $spacing.sm
        gap: $spacing.md
  tokens:
    background: $color_scheme.light.color.surface
  children:
    - user-detail-header
    - user-detail-info
    - user-detail-profile
    - user-detail-roles

# ── Header ───────────────────────────────────────────────────────────────────

user-detail-header:
  type: PageHeader
  back_route: /admin/users
  title_prefix: $t.admin.users.detail_title_prefix
  bind:
    content: user.username

# ── User info card ───────────────────────────────────────────────────────────

user-detail-info:
  type: Section
  spatial:
    display: flex
    direction: column
    gap: $spacing.md
    padding: $spacing.lg
  tokens:
    background: $color_scheme.light.color.surface-elevated
    radius: $radius.lg
    shadow: $color_scheme.light.shadow.sm
  children:
    - user-info-section-title
    - user-info-username
    - user-info-email
    - user-info-status

user-info-section-title:
  type: Text
  content: $t.admin.users.section_info
  a11y:
    role: heading
    level: 2
  tokens:
    font_size: $font.size.lg
    color: $color_scheme.light.color.on-surface

user-info-username:
  type: Section
  spatial:
    display: flex
    gap: $spacing.sm
  children:
    - user-info-username-label
    - user-info-username-value

user-info-username-label:
  type: Text
  content: $t.admin.users.label_username
  tokens:
    font_size: $font.size.sm
    color: $color_scheme.light.color.on-surface-muted

user-info-username-value:
  type: Text
  bind:
    content: user.username
  tokens:
    font_size: $font.size.sm

user-info-email:
  type: Section
  spatial:
    display: flex
    gap: $spacing.sm
  children:
    - user-info-email-label
    - user-info-email-value

user-info-email-label:
  type: Text
  content: $t.admin.users.label_email
  tokens:
    font_size: $font.size.sm
    color: $color_scheme.light.color.on-surface-muted

user-info-email-value:
  type: Text
  bind:
    content: user.email
  tokens:
    font_size: $font.size.sm

user-info-status:
  type: Section
  spatial:
    display: flex
    gap: $spacing.sm
  children:
    - user-info-status-label
    - user-info-status-value

user-info-status-label:
  type: Text
  content: $t.admin.users.label_status
  tokens:
    font_size: $font.size.sm
    color: $color_scheme.light.color.on-surface-muted

user-info-status-value:
  type: Badge
  bind:
    content: user.enabled

# ── Profile card ─────────────────────────────────────────────────────────────

user-detail-profile:
  type: Section
  spatial:
    display: flex
    direction: column
    gap: $spacing.md
    padding: $spacing.lg
  tokens:
    background: $color_scheme.light.color.surface-elevated
    radius: $radius.lg
    shadow: $color_scheme.light.shadow.sm
  children:
    - user-profile-section-title
    - user-profile-language
    - user-profile-timezone
    - user-profile-theme

user-profile-section-title:
  type: Text
  content: $t.admin.users.section_profile
  a11y:
    role: heading
    level: 2
  tokens:
    font_size: $font.size.lg
    color: $color_scheme.light.color.on-surface

user-profile-language:
  type: Section
  spatial:
    display: flex
    gap: $spacing.sm
  children:
    - user-profile-lang-label
    - user-profile-lang-value

user-profile-lang-label:
  type: Text
  content: $t.admin.users.label_language
  tokens:
    font_size: $font.size.sm
    color: $color_scheme.light.color.on-surface-muted

# The model's value_labels name a chosen language; a null one (never chosen, which the
# DTO carries as null) misses that lookup and falls back to the same "Browser language" label.
user-profile-lang-value:
  type: Text
  content: $t.profile.language.auto
  bind:
    content: user.profile.language
  tokens:
    font_size: $font.size.sm

user-profile-timezone:
  type: Section
  spatial:
    display: flex
    gap: $spacing.sm
  children:
    - user-profile-tz-label
    - user-profile-tz-value

user-profile-tz-label:
  type: Text
  content: $t.admin.users.label_timezone
  tokens:
    font_size: $font.size.sm
    color: $color_scheme.light.color.on-surface-muted

user-profile-tz-value:
  type: Text
  bind:
    content: user.profile.timezone
  tokens:
    font_size: $font.size.sm

user-profile-theme:
  type: Section
  spatial:
    display: flex
    gap: $spacing.sm
  children:
    - user-profile-theme-label
    - user-profile-theme-value

user-profile-theme-label:
  type: Text
  content: $t.admin.users.label_theme
  tokens:
    font_size: $font.size.sm
    color: $color_scheme.light.color.on-surface-muted

user-profile-theme-value:
  type: Text
  bind:
    content: user.profile.theme
  tokens:
    font_size: $font.size.sm

# ── Roles section ────────────────────────────────────────────────────────────

user-detail-roles:
  type: Section
  spatial:
    display: flex
    direction: column
    gap: $spacing.md
    padding: $spacing.lg
  tokens:
    background: $color_scheme.light.color.surface-elevated
    radius: $radius.lg
    shadow: $color_scheme.light.shadow.sm
  children:
    - user-roles-section-title
    - user-roles-table
    - user-roles-assign-form
    - user-roles-remove-gate

user-roles-section-title:
  type: Text
  content: $t.admin.users.section_roles
  a11y:
    role: heading
    level: 2
  tokens:
    font_size: $font.size.lg
    color: $color_scheme.light.color.on-surface

user-roles-table:
  type: Table
  bind:
    rows: heldRoles
  a11y:
    label: $t.admin.users.roles_table_label
  columns:
    - key: roleName
      label: $t.admin.users.col_role
      width: 2fr
    - key: scope
      label: $t.admin.users.col_scope
      width: 2fr
  empty: user-roles-empty

user-roles-empty:
  type: EmptyState
  content: $t.admin.users.no_roles
  body: $t.admin.users.no_roles_body

user-roles-assign-form:
  type: Section
  spatial:
    display: flex
    direction: column
    gap: $spacing.sm
  children:
    - user-role-name-input
    - user-role-assign-btn

# Role picker. Dropdown of the roles that can be granted
# (GET /api/users/roles?assignable=true). Option value + label = role name (RoleDto.id mirrors
# name). One role per Assign; user picks-then-adds, repeats for multiple roles.
user-role-name-input:
  type: Input
  input_type: select
  label: $t.admin.users.assign_role_name
  bind:
    value: assignForm.roleName
    options: roles
  a11y:
    label: $t.admin.users.assign_role_name_label
  validation:
    required: true
  spatial:
    flex: 1
    min_height: $size.touch-target

user-role-assign-btn:
  type: Button
  color: info
  content: $t.admin.users.assign_role
  a11y:
    label: $t.admin.users.assign_role_label
  action:
    type: custom
    method: POST
    url: /api/users/:userId/roles
    body: assignForm
  spatial:
    min_height: $size.touch-target
    padding_inline: $spacing.lg
  tokens:
    radius: $radius.md
  states:
    hover:
      background: $color_scheme.light.color.info-text

# Unassign a role. Pick the assignment — the role and the domain it is held on —
# confirm, DELETE /api/users/{userId}/roles/{assignmentId} (gate
# RoleAssignment:delete), table refreshes via the action's xp85-data-changed
# dispatch. Gated section mirrors the role-detail delete-gate pattern.
user-roles-remove-gate:
  type: Section
  permission: "RoleAssignment:delete"
  children:
    - user-roles-remove-form

user-roles-remove-form:
  type: Section
  spatial:
    display: flex
    direction: column
    gap: $spacing.sm
  children:
    - user-role-remove-input
    - user-role-remove-btn

user-role-remove-input:
  type: Input
  input_type: select
  label: $t.admin.users.remove_role_name
  bind:
    value: removeForm.assignmentId
    options: heldRoles
  a11y:
    label: $t.admin.users.remove_role_name_label
  # Required: the action writes the picked id into its URL, so an empty selection has no
  # assignment to address. The field is announced and marked as required, and the action itself
  # sends nothing while the address it reads from page data is empty.
  validation:
    required: true
  spatial:
    flex: 1
    min_height: $size.touch-target

user-role-remove-btn:
  type: Button
  color: stop
  content: $t.admin.users.remove_role
  a11y:
    label: $t.admin.users.remove_role_label
  action:
    type: custom
    method: DELETE
    # URL uses $data.* prefix (ActionEmitter.ResolveNavigateTarget) which
    # resolves each path segment against this.data at click time. The input
    # bind above uses the plain `removeForm.assignmentId` form-binding convention
    # (no $data. prefix); both are correct — different syntaxes for different
    # emitter contexts (URL interpolation vs form bind context). The value is
    # the picked assignment's own id, {roleName}@{domainId}, written into the URL
    # as one encoded segment.
    url: /api/users/:userId/roles/$data.removeForm.assignmentId
    confirm: $t.admin.users.remove_role_confirm
  spatial:
    min_height: $size.touch-target
    padding_inline: $spacing.lg
  tokens:
    radius: $radius.md
